"use client";

import AutoPlay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useState } from "react";

type Props = {
  slides: React.ReactNode[];
  slidesPerView?: number;
  breakpoints?: {
    [width: number]: number;
  };
  showArrows?: boolean;
  showDots?: boolean;
  renderPrevArrow?: (scrollPrev: () => void) => React.ReactNode;
  renderNextArrow?: (scrollNext: () => void) => React.ReactNode;
  renderDot?: (props: {
    index: number;
    isActive: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  options?: Parameters<typeof useEmblaCarousel>[0];
  onSlideChange?: (index: number) => void;
  className?: string;
  slideClassName?: string;
  autoPlay?: boolean;
  delay?: number;
};

export const Slider: React.FC<Props> = ({
  slides,
  slidesPerView = 1,
  breakpoints = {},
  showArrows = false,
  showDots = false,
  renderPrevArrow,
  renderNextArrow,
  renderDot,
  options,
  onSlideChange,
  className = "",
  slideClassName = "",
  autoPlay,
  delay = 1000,
}) => {
  const locale = useLocale();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    options || {
      align: "start",
      dragFree: false,
      
      loop: true,
      direction: locale === "en" ? "ltr" : "rtl",
    },
    [AutoPlay({ playOnInit: autoPlay, delay })]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(slidesPerView);

  const updateSlidesPerView = useCallback(() => {
    const width = window.innerWidth;
    let newSlidesPerView = slidesPerView;
    Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((bp) => {
        if (width >= bp) {
          newSlidesPerView = breakpoints[bp];
        }
      });
    setVisibleSlides(newSlidesPerView);
  }, [breakpoints, slidesPerView]);

  useEffect(() => {
    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, [updateSlidesPerView]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setCurrentIndex(index);
      if (onSlideChange) {
        onSlideChange(index);
      }
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSlideChange]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (index: number) => emblaApi?.scrollTo(index);

  const slideCount = slides.length - visibleSlides + 1;

  return (
    <div className={`relative w-full ${className}`}>
      <div
        className={`h-full cursor-grab overflow-hidden ${slideClassName}`}
        ref={emblaRef}
      >
        <div className="flex" style={{ gap: "1rem" }}>
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className="flex-[0_0_auto]"
              style={{ width: `${100 / visibleSlides}%` }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {showArrows ? (
        <>
          {renderPrevArrow ? (
            renderPrevArrow(scrollPrev)
          ) : (
            <button
              onClick={scrollPrev}
              className="absolute top-1/2 left-2 z-10 -translate-y-1/2 bg-black/60 px-2 py-1 text-white"
            >
              ‹
            </button>
          )}
          {renderNextArrow ? (
            renderNextArrow(scrollNext)
          ) : (
            <button
              onClick={scrollNext}
              className="absolute top-1/2 right-2 z-10 -translate-y-1/2 bg-black/60 px-2 py-1 text-white"
            >
              ›
            </button>
          )}
        </>
      ) : null}


      {/* Dots */}
      {showDots ? (
        <div className="flex pb-20 pt-10 sm:pt-0 justify-center gap-4">
          {Array.from({ length: slideCount }).map((_, idx) =>
            renderDot ? (
              renderDot({
                index: idx,
                isActive: currentIndex === idx,
                onClick: () => scrollTo(idx),
              })
            ) : (
              <button
                key={idx}
                aria-label={`ariaLabels.goToSlide`}
                onClick={() => scrollTo(idx)}
                className={` h-2 bg-secondary w-6 cursor-pointer rounded-full transition-all ${
                  currentIndex === idx ? "bg-secondary w-10" : "bg-secondary/50"
                }`}
              />
            )
          )}
        </div>
      ) : null}
    </div>
  );
};
